import type { PokemonV2 } from '../pokemon/pokemon';
import {
  MOVE_DAMAGE_CLASS_STATUS,
  MOVE_DAMAGE_CLASS_PHYSICAL,
  MOVE_DAMAGE_CLASS_SPECIAL,
} from '../move/move';
import type { Move } from '../move/move';
import move_list from '../move/move-list';

const getUsageIndex = (pokemon: PokemonV2, moveId: number): number => {
  const index = pokemon.often_used_move.indexOf(moveId);
  // 使用率リストに無い技は末尾扱いにする
  return index >= 0 ? index : 999;
};

// IDから技を取得
export const getMoveById = (moveId: number): Move | undefined => {
  return move_list.find((m) => m.id === moveId);
};

// 指定ID群の技を取得（存在しないIDは除外）
export const getMovesByIds = (moveIds: number[]): Move[] => {
  return moveIds
    .map((moveId) => getMoveById(moveId))
    .filter((move): move is Move => move !== undefined);
};

type PokemonMoveOptions = {
  includeStatus?: boolean;
};

// 指定したポケモンが覚える技一覧（使用率順）
export const getPokemonMovesByUsage = (
  pokemon: PokemonV2,
  options: PokemonMoveOptions = {}
): Move[] => {
  const includeStatus = options.includeStatus ?? false;

  return move_list
    .filter((move) => {
      if (!pokemon.move_id_list.includes(move.id)) {
        return false;
      }
      if (!includeStatus && move.damage_class_number === MOVE_DAMAGE_CLASS_STATUS) {
        return false;
      }
      return true;
    })
    .sort((a, b) => getUsageIndex(pokemon, a.id) - getUsageIndex(pokemon, b.id));
};

// ポケモンが覚える攻撃技（物理/特殊のみ）を取得（TOP10を優先）
export const getAllAttackMoves = (pokemon: PokemonV2): Move[] => {
  const topMoveIds = pokemon.often_used_move.slice(0, 10);
  const topMoves = topMoveIds
    .map((moveId) => move_list.find((move) => move.id === moveId))
    .filter((move): move is Move => move !== undefined)
    .filter(
      (move) =>
        move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL ||
        move.damage_class_number === MOVE_DAMAGE_CLASS_SPECIAL
    );

  const allOtherMoves = move_list
    // 使用率上位以外は名前順で探しやすく並べる
    .filter(
      (move) =>
        pokemon.move_id_list.includes(move.id) &&
        (move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL ||
          move.damage_class_number === MOVE_DAMAGE_CLASS_SPECIAL) &&
        !topMoveIds.includes(move.id)
    )
    .sort((a, b) => a.name_ja.localeCompare(b.name_ja));

  return [...topMoves, ...allOtherMoves];
};
